import {
  APIBARA_COLORS,
  APIBARA_DAMAGE_TYPES,
  APIBARA_DRIVE_TYPES,
  APIBARA_FUEL_TYPES,
  APIBARA_KEYS_OPTIONS,
  APIBARA_RUN_CONDITIONS,
  APIBARA_TRANSMISSIONS,
  AUCTION_HOUSE_LABELS,
  AUCTION_MARKET_LABELS,
  getAuctionDateWindow,
  getPriceBasisOption,
  resolveMarketCurrency,
} from './auctionConfig';
import { APIBARA_FILTER_PARAMS, type TApibaraFilters } from './apibaraFilters';
import { DEFAULT_LOT_SORT, FILTER_PARAMS, lotSortOptions, type TLotFilters } from './lotFilters';

/**
 * The applied-filter chips for a listing (spec 013, R6).
 *
 * Pure: reads the URL-derived filters and nothing else, so the chip row can
 * never describe an edited-but-unsubmitted field as if it were filtering the
 * list. Lives outside the form because the form now has two homes — a sidebar
 * and a drawer — and the chips must stay visible above the results whichever
 * home is closed. The same list gives the "Filtry · N" trigger its count, so
 * the two can never disagree.
 *
 * Every filter with a control has a chip, including the condition/equipment
 * and auction-house ones that had none before; a count that included them but
 * a row that hid them would be a quiet inaccuracy (Principle I).
 */

export type TAppliedFilterChip = {
  key: string;
  label: string;
  /** Address parameters removing this chip deletes. */
  removes: string[];
};

export type TApibaraExtras = Omit<TApibaraFilters, keyof TLotFilters>;

const formatAmount = (amount: number) => amount.toLocaleString('pl-PL');

/** The Polish label the select shows for a provider value, or the raw value. */
const labelFor = (options: readonly { value: string; label: string }[], value: string) =>
  options.find(option => option.value === value)?.label ?? value;

const rangeLabel = (from: number | null, to: number | null, unit: string) =>
  from !== null && to !== null
    ? `${formatAmount(from)}–${formatAmount(to)} ${unit}`
    : from !== null
      ? `od ${formatAmount(from)} ${unit}`
      : `do ${formatAmount(to as number)} ${unit}`;

export const getAppliedFilterChips = ({
  filters,
  extras,
}: {
  filters: TLotFilters;
  extras: TApibaraExtras;
}): TAppliedFilterChip[] => {
  const chips: TAppliedFilterChip[] = [];
  const p = APIBARA_FILTER_PARAMS;
  const sold = filters.mode !== 'live';

  // A lookup replaces every other filter, so it is the only chip.
  if (filters.lookup) {
    return [{ key: 'lookup', label: `Szukaj: ${filters.lookup}`, removes: [FILTER_PARAMS.lookup, p.cursor] }];
  }

  // Removing the mode also drops the cursor — a position in the OTHER result set.
  if (filters.mode !== 'live') {
    chips.push({ key: 'mode', label: filters.mode === 'sold' ? 'Sprzedane' : 'Na żywo', removes: [FILTER_PARAMS.mode, p.cursor] });
  }

  if (filters.make) {
    // Removing the make orphans the model, which is scoped to it.
    chips.push({
      key: 'make',
      label: `Marka: ${filters.make}`,
      removes: [FILTER_PARAMS.make, FILTER_PARAMS.model],
    });
  }

  if (filters.model) {
    chips.push({ key: 'model', label: `Model: ${filters.model}`, removes: [FILTER_PARAMS.model] });
  }

  if (filters.yearFrom || filters.yearTo) {
    const range =
      filters.yearFrom && filters.yearTo
        ? `${filters.yearFrom}–${filters.yearTo}`
        : filters.yearFrom
          ? `od ${filters.yearFrom}`
          : `do ${filters.yearTo}`;

    chips.push({
      key: 'year',
      label: `Rocznik: ${range}`,
      removes: [FILTER_PARAMS.yearFrom, FILTER_PARAMS.yearTo],
    });
  }

  if (filters.priceFrom !== null || filters.priceTo !== null) {
    // No market chosen means the range spanned both currencies.
    const unit = resolveMarketCurrency(filters.markets) ?? 'USD/CAD';

    chips.push({
      key: 'price',
      label: `${getPriceBasisOption(filters.priceBasis).label}: ${rangeLabel(filters.priceFrom, filters.priceTo, unit)}`,
      removes: [FILTER_PARAMS.priceFrom, FILTER_PARAMS.priceTo, FILTER_PARAMS.priceBasis],
    });
  }

  filters.markets.forEach(entry =>
    chips.push({
      key: `market-${entry}`,
      label: `Rynek: ${AUCTION_MARKET_LABELS[entry]}`,
      removes: [FILTER_PARAMS.market],
    })
  );

  filters.houses.forEach(house =>
    chips.push({
      key: `house-${house}`,
      label: `Dom aukcyjny: ${AUCTION_HOUSE_LABELS[house]}`,
      removes: [FILTER_PARAMS.house],
    })
  );

  if (filters.auctionWindow && !sold) {
    chips.push({
      key: 'auction-window',
      label: `Termin aukcji: ${getAuctionDateWindow(filters.auctionWindow).label}`,
      removes: [FILTER_PARAMS.auctionWindow],
    });
  }

  if (filters.buyNowOnly && !sold) {
    chips.push({ key: 'buy-now', label: 'Tylko „Kup teraz”', removes: [FILTER_PARAMS.buyNowOnly] });
  }

  if (extras.runCondition) {
    chips.push({
      key: 'run-condition',
      label: `Stan: ${labelFor(APIBARA_RUN_CONDITIONS, extras.runCondition)}`,
      removes: [p.runCondition],
    });
  }

  extras.damage.forEach(value =>
    chips.push({
      key: `damage-${value}`,
      label: `Szkoda: ${labelFor(APIBARA_DAMAGE_TYPES, value)}`,
      removes: [p.damage],
    })
  );

  extras.driveType.forEach(value =>
    chips.push({
      key: `drive-${value}`,
      label: `Napęd: ${labelFor(APIBARA_DRIVE_TYPES, value)}`,
      removes: [p.driveType],
    })
  );

  extras.transmission.forEach(value =>
    chips.push({
      key: `transmission-${value}`,
      label: `Skrzynia: ${labelFor(APIBARA_TRANSMISSIONS, value)}`,
      removes: [p.transmission],
    })
  );

  extras.fuel.forEach(value =>
    chips.push({
      key: `fuel-${value}`,
      label: `Paliwo: ${labelFor(APIBARA_FUEL_TYPES, value)}`,
      removes: [p.fuel],
    })
  );

  extras.color.forEach(value =>
    chips.push({
      key: `color-${value}`,
      label: `Kolor: ${labelFor(APIBARA_COLORS, value)}`,
      removes: [p.color],
    })
  );

  if (extras.keys) {
    chips.push({
      key: 'keys',
      label: labelFor(APIBARA_KEYS_OPTIONS, extras.keys),
      removes: [p.keys],
    });
  }

  // A non-default order is a filtered view (spec 017): noindex, and said above the results.
  if (filters.sort !== DEFAULT_LOT_SORT) {
    chips.push({
      key: 'sort',
      label: `Sortowanie: ${lotSortOptions(filters.mode).find(option => option.value === filters.sort)?.label ?? filters.sort}`,
      removes: [FILTER_PARAMS.sort],
    });
  }

  if (extras.odometerFrom !== null || extras.odometerTo !== null) {
    chips.push({
      key: 'odometer',
      label: `Przebieg: ${rangeLabel(extras.odometerFrom, extras.odometerTo, 'mi')}`,
      removes: [p.odometerFrom, p.odometerTo],
    });
  }

  return chips;
};
